import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createTemplate,
  deleteTemplate,
  getTemplate,
  listTemplateSummaries,
  listTemplates,
  setDefaultTemplate,
  updateTemplate,
} from '@/api/clt'
import {
  fromApiTemplate,
  fromApiTemplateSummary,
  toApiTemplate,
  type UiCalendarLogicTemplate,
} from '@/api/clt.adapter'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

const KEY = ['clt'] as const
const SUMMARY_KEY = ['clt', 'summary'] as const

export function useTemplateSummaries() {
  return useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: async () =>
      (await listTemplateSummaries()).map(fromApiTemplateSummary),
  })
}

export function useTemplates(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await listTemplates()).map(fromApiTemplate),
    enabled: options?.enabled ?? true,
  })
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, id],
    queryFn: async () => fromApiTemplate(await getTemplate(id as string)),
    enabled: !!id,
  })
}

/**
 * Create-or-update mutation. Determines which by whether the UI template has
 * an id that is already known to the backend (we treat the wizard-generated
 * placeholder ids as create — the backend will issue a real id).
 */
export function useUpsertTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ui: UiCalendarLogicTemplate) => {
      const payload = toApiTemplate(ui)
      // Treat ids that look like client-generated placeholders ("clt-xxxxxxxx"
      // from `crypto.randomUUID().slice(0, 8)`) as new creates. Backend ids are
      // longer (UUIDs). Wizard `submit()` sets editingId only when editing an
      // existing template; we use the presence of that as the canonical signal.
      // Caller is expected to set ui.id to the backend id when editing.
      const looksLikePlaceholder =
        !ui.id || /^clt-[a-z0-9]{1,12}$/i.test(ui.id) || ui.id.length < 16
      return looksLikePlaceholder
        ? createTemplate(payload)
        : updateTemplate(ui.id, payload)
    },
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: KEY })
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY })
      queryClient.removeQueries({ queryKey: [...KEY, result.id] })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to save template'))
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY })
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete template'))
    },
  })
}

export function useSetDefaultTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => setDefaultTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY })
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to set default template'))
    },
  })
}
